import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../commonComponents/header/Header.js";
import { Formik, Form } from "formik";
import * as Yup from "yup";

// images
import interViewLoinImg from "../../../assets/images/login-page-left-img.svg";
import emailImg from "../../../assets/images/email-icon.svg";
import arrowWhiteImg from "../../../assets/images/arrow-white-icon.svg";
import { initJdId } from "../../../utils/decryptURL.js";
import Footer from "../../commonComponents/footer/Footer.js";
import LoadingDialog from "../../commonComponents/loader/LoadingDialog.js";
import CommonSnackbar from "../../commonComponents/snackbar/CommonSnackbar.js";
import { maskText } from "../../commonComponents/helperFunctions/HelperFunction.js";
import AccessDenied from "./AccessDenied.js";
import { Turnstile } from "@marsidev/react-turnstile";

const ScreenupLogin = () => {
  const navigate = useNavigate();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [emailForLoader, setEmailForLoader] = useState("");
  const [hasPermission, setHasPermission] = useState(null); // null = checking, true = has permission, false = no permission
  const [apiAlive, setApiAlive] = useState(false);
  const [warmingUp, setWarmingUp] = useState(false);
  const [prepSecondsLeft, setPrepSecondsLeft] = useState(null); // 120 seconds timer
  const [warmupFailed, setWarmupFailed] = useState(false);
  const [urlReady, setUrlReady] = useState(false);
  const [urlValid, setUrlValid] = useState(false);

  const [captchaMessage, setCaptchaMessage] = useState(
    "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const jdId = params.get("jdId");
    const companyId = params.get("companyId");

    if (jdId && companyId) {
      sessionStorage.setItem("jdId", jdId);
      sessionStorage.setItem("companyId", companyId);
      setUrlValid(true);
    } else {
      setUrlValid(false);
    }

    setUrlReady(true);
  }, []);
  useEffect(() => {
    let isMounted = true;

    const checkPermission = async () => {
      // Check if jdId exists in URL parameters
      const params = new URLSearchParams(window.location.search);
      const jdId = params.get("jdId");

      if (!jdId) {
        setHasPermission(false);
        return;
      }

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Warmup helper for URL (6 retries)
      const warmupUrl = async (url, maxAttempts = 6, timeoutMs = 15000) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

            const res = await fetch(url, {
              method: "GET",
              signal: controller.signal,
              cache: "no-store",
            });

            clearTimeout(timeoutId);

            if (res.ok) return true;
          } catch (err) {
            // ignore and retry
          }

          await delay(attempt * 1000);
        }
        return false;
      };

      //  single ping check (used during 2-min countdown)
      const checkOnce = async (url) => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);

          const res = await fetch(url, {
            method: "GET",
            signal: controller.signal,
            cache: "no-store",
          });

          clearTimeout(timeoutId);
          return res.ok;
        } catch (e) {
          return false;
        }
      };

      try {
        await initJdId();
        if (!isMounted) return;

        setHasPermission(true);

        //  overlay ON
        setWarmingUp(true);
        setApiAlive(false);
        setWarmupFailed(false);
        setPrepSecondsLeft(null);

        const healthUrl = `${process.env.REACT_APP_API_ROOT_URL}/health`;

        // Phase 1: 6 retries
        const alive = await warmupUrl(healthUrl, 6, 15000);

        if (!isMounted) return;

        if (alive) {
          setApiAlive(true);
          setWarmingUp(false);
          return;
        }

        // Phase 2: 2-min countdown + polling
        setPrepSecondsLeft(120);

        const endAt = Date.now() + 120 * 1000;
        let healthyFound = false;

        const countdownInterval = setInterval(() => {
          const remainingMs = endAt - Date.now();
          const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));

          if (!isMounted) return;

          setPrepSecondsLeft(remainingSec);

          if (remainingSec <= 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);

        const pollInterval = setInterval(async () => {
          if (Date.now() > endAt || healthyFound) return;

          const ok = await checkOnce(healthUrl);
          if (!isMounted) return;

          if (ok) {
            healthyFound = true;
            setApiAlive(true);
            setWarmingUp(false);
            setPrepSecondsLeft(null);
            clearInterval(pollInterval);
            clearInterval(countdownInterval);
          }
        }, 6000);

        setTimeout(() => {
          if (!isMounted) return;

          if (!healthyFound) {
            setWarmupFailed(true);
          }

          clearInterval(pollInterval);
          clearInterval(countdownInterval);
        }, 120 * 1000);
      } catch (error) {
        console.error("Error initializing jdId:", error);
        if (!isMounted) return;

        setHasPermission(false);
        setApiAlive(false);
        setWarmingUp(false);
        setWarmupFailed(true);
      }
    };

    checkPermission();

    return () => {
      isMounted = false;
    };
  }, []);
  const isCaptchaDisabled = process.env.REACT_APP_DISABLE_CAPTCHA === "true";
  const jdId = sessionStorage.getItem("jdId");

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      sessionStorage.setItem("screenup_email", values.email);
      setEmailForLoader(values.email);

      // Turnstile check
      if (!turnstileToken && !isCaptchaDisabled) {
        setSnackbar({
          message: "Please complete the security verification.",
          type: "failure",
        });
        setSubmitting(false);
        return;
      }

      setLoading(true);

      const companyId = sessionStorage.getItem("companyId") || "";

      //  send OTP directly
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/screenup/send-otp`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: values.email,
            company_id: companyId,
            jd_id: jdId,
          }),
        },
      );

      if (!response.ok) {
        let errorMessage = "Failed to send OTP";

        try {
          const errorData = await response.json();
          errorMessage =
            errorData?.detail || errorData?.message || errorMessage;
        } catch (e) {
          // fallback if response is not JSON
        }

        throw new Error(errorMessage);
      }

      setSnackbar({
        message: "OTP sent successfully!",
        type: "success",
      });

      //  go directly to OTP
      setTimeout(() => {
        navigate("/otp", { state: { email: values.email } });
      }, 1200);
    } catch (error) {
      setFieldError("email", "Failed to send OTP.");
      setSnackbar({
        message: error.message,
        type: "failure",
      });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const initialValues = {
    email: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .trim() // removes leading/trailing spaces
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Enter a valid email",
      ) // Safari-compatible & case-safe
      .required("Email is required"),
  });
  if (!urlReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  if (!urlValid) {
    return <AccessDenied />;
  }
  // Show loading state while checking permission
  if (hasPermission === null) {
    return (
      <div className="flex flex-col min-h-screen bg-white pt-4 px-4">
        <Header />

        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logoColor mx-auto mb-4"></div>
            <p className="text-fontColor text-lg">Verifying access...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show permission denied screen
  if (hasPermission === false) {
    return <AccessDenied />;
  }

  // Render normal login form if permission is granted
  return (
    <div className="flex flex-col min-h-screen bg-white pt-4 px-4">
      {loading && (
        <LoadingDialog
          title="Sending Verification Code"
          primaryMSG="Sending OTP to your"
          email={
            <span className="loading-highlight">
              {maskText(emailForLoader)}
            </span>
          }
          bottomMsg="Just a moment."
          // message={`Verifying Email: ${maskText(emailForLoader)}`}
        />
      )}
      {snackbar && (
        <CommonSnackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}
      <Header />
      {warmingUp && (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center px-4">
          {!warmupFailed ? (
            <>
              <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-logoColor mb-5"></div>

              <p className="text-fontColor text-lg font-semibold text-center">
                {prepSecondsLeft !== null
                  ? "Preparing acceptance form"
                  : "Preparing your acceptance screen..."}
              </p>

              <p className="text-fontColor text-sm mt-2 opacity-70 text-center max-w-[340px]">
                {prepSecondsLeft !== null
                  ? "This may take up to the time shown below. Please don’t refresh the page."
                  : "Connecting securely and starting required services..."}
              </p>

              {prepSecondsLeft !== null && (
                <div className="mt-4 text-fontColor text-base font-medium">
                  Should be ready in{" "}
                  <span className="font-bold">
                    {String(Math.floor(prepSecondsLeft / 60)).padStart(2, "0")}:
                    {String(prepSecondsLeft % 60).padStart(2, "0")}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              {/*  No loader when retry appears */}
              <p className="text-fontColor text-lg font-semibold text-center">
                We’re facing high traffic right now
              </p>
              <p className="text-fontColor text-sm mt-2 opacity-70 text-center max-w-[360px]">
                The assessment services are taking longer than expected. Please
                Please try again in a few minutes.
              </p>

              <button
                onClick={() => window.location.reload()}
                className="mt-6 px-6 py-2 rounded-xl bg-btnBgColor text-white"
              >
                Retry
              </button>
            </>
          )}
        </div>
      )}
      <Formik
        initialValues={{ email: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          values,
          handleChange,
          handleBlur,
          isSubmitting,
          isValid,
          dirty,
          submitForm,
        }) => {
          const disabled =
            loading ||
            isSubmitting ||
            !dirty ||
            !isValid ||
            warmingUp ||
            !apiAlive;

          return (
            <>
              {/* -------- Main Content -------- */}
              <div className="flex-1 flex flex-col md:flex-row overflow-y-auto">
                {/* Left Image — hidden on mobile */}
                {/* <div className="hidden md:flex w-full md:w-1/2 justify-center items-center pl-[34px] pr-[50px]"></div> */}
                <div className="mt-10 md:mt-0 md:flex w-full md:w-1/2 justify-center items-center pl-[34px] pr-[50px]">
                  <img
                    src={interViewLoinImg}
                    alt="login"
                    className="object-contain w-[100%] h-auto rounded-3xl"
                  />
                </div>

                {/* Divider */}
                <div className="hidden md:block border-l border-dividerColor my-[5%]" />

                {/* Right Form */}
                <div className="w-full md:w-1/2 flex justify-center items-center px-6 py-8 md:p-[70px]">
                  <div className="w-full md:w-[100%]">
                    <h2 className="app-title font-medium mb-1 text-fontColor">
                      Welcome!
                    </h2>
                    <p className="app-body text-fontColor">
                      To apply for the opportunity, please provide your Email.
                    </p>

                    <Form className="mt-12">
                      <Form className="mt-12">
                        <div className="flex items-center gap-2 mb-8 w-full sm:w-[80%] md:w-[70%]">
                          <img src={emailImg} alt="email" className="h-4" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="Email ID"
                            value={values.email}
                            onChange={(e) => {
                              e.target.value = e.target.value.trimStart();
                              handleChange(e);
                              setEmailForLoader(e.target.value);
                            }}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.keyCode === 13) {
                                e.preventDefault();
                                if (!disabled) {
                                  submitForm();
                                }
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter" || e.keyCode === 13) {
                                e.preventDefault();
                                if (!disabled) {
                                  submitForm();
                                }
                              }
                            }}
                            className="border-b border-logoColor focus:outline-none text-primaryFontSize w-full text-fontColor"
                          />
                        </div>

                        {/*  Cloudflare Turnstile */}
                        <div>
                          <p className="app-body text-fontColor">
                            {captchaMessage}
                          </p>

                          <Turnstile
                            key={turnstileKey}
                            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                            onSuccess={(token) => {
                              setTurnstileToken(token);
                              setCaptchaMessage(
                                "Verification successful. Please click on Proceed.",
                              );
                            }}
                            onExpire={() => {
                              setTurnstileToken(null);
                              setCaptchaMessage(
                                "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
                              );
                            }}
                            onError={() => {
                              setTurnstileToken(null);
                              setTurnstileKey((k) => k + 1);
                              setCaptchaMessage(
                                "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
                              );
                            }}
                            options={{
                              theme: "light",
                              size: "normal",
                              appearance: "always",
                            }}
                          />
                        </div>
                      </Form>
                    </Form>
                  </div>
                </div>
              </div>

              {/* -------- Bottom Submit Button -------- */}
              <div className="mt-auto w-full flex justify-end pb-4 ">
                <button
                  type="button"
                  onClick={submitForm}
                  disabled={disabled}
                  className="flex items-center px-6 py-1.5 btn-size transition-colors duration-200 bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed mr-[36px]"
                >
                  Proceed
                  <img src={arrowWhiteImg} alt="arrow" className="ml-2" />
                </button>
              </div>
            </>
          );
        }}
      </Formik>
      <Footer />
    </div>
  );
};

export default ScreenupLogin;
